// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useEffect } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountApi } from '@/lib/api/account'
import { queryKeys } from '@/constants/queryKeys'
import { notificationsWebSocketUrl } from '@/lib/notificationsWebSocketUrl'
import type { NotificationPreferencesUpdate } from '@/types'

export function useOrganization() {
  return useQuery({
    queryKey: queryKeys.account.organization(),
    queryFn: () => accountApi.getOrganization(),
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name?: string }) => accountApi.updateOrganization(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.organization() })
    },
  })
}

export function useAccountProfile() {
  return useQuery({
    queryKey: queryKeys.account.profile(),
    queryFn: () => accountApi.getProfile(),
  })
}

export function useTeam() {
  return useQuery({
    queryKey: queryKeys.account.team(),
    queryFn: () => accountApi.getTeam(),
  })
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      accountApi.updateMemberRole(userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.team() })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => accountApi.removeMember(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.team() })
    },
  })
}

export function useInvites(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.account.invites(),
    queryFn: () => accountApi.getInvites(),
    enabled: options?.enabled ?? true,
  })
}

export function useCreateInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { email: string; role: string }) => accountApi.createInvite(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.invites() })
    },
  })
}

export function useCancelInvite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) => accountApi.cancelInvite(inviteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.invites() })
    },
  })
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.account.sessions(),
    queryFn: () => accountApi.getSessions(),
  })
}

export function useRevokeSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) => accountApi.revokeSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.sessions() })
    },
  })
}

export function useRevokeAllSessions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => accountApi.revokeAllSessions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.sessions() })
    },
  })
}

export function useMfaTotpSetup() {
  return useMutation({ mutationFn: () => accountApi.mfaTotpSetup() })
}

export function useMfaTotpVerify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => accountApi.mfaTotpVerify(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.profile() })
    },
  })
}

export function useMfaTotpDisable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { password?: string; code?: string }) => accountApi.mfaTotpDisable(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.profile() })
    },
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: queryKeys.account.notificationPreferences(),
    queryFn: () => accountApi.getNotificationPreferences(),
  })
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: NotificationPreferencesUpdate) =>
      accountApi.updateNotificationPreferences(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notificationPreferences() })
    },
  })
}

export function useInAppUnreadCount() {
  return useQuery({
    queryKey: queryKeys.account.notificationsUnread(),
    queryFn: () => accountApi.getInAppUnreadCount(),
    /** WebSocket delivers updates; slow polling as fallback if connection is down. */
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Maintains a WebSocket to the API; on `notification.*` payloads invalidates notification queries.
 * Uses short-lived handshake tokens (POST ws-token); reconnects before expiry.
 */
export function useInAppNotificationsWebSocket(enabled: boolean) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return

    let disposed = false
    let ws: WebSocket | null = null
    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined

    const clearTimers = () => {
      if (refreshTimer) clearTimeout(refreshTimer)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      refreshTimer = undefined
      reconnectTimer = undefined
    }

    const connect = async () => {
      if (disposed) return
      clearTimers()
      try {
        const { token, expiresAt } = await accountApi.getNotificationWsToken()
        if (disposed) return

        const url = notificationsWebSocketUrl(token)
        ws = new WebSocket(url)

        const expMs = new Date(expiresAt).getTime() - Date.now()
        const refreshMs = Math.max(5_000, expMs - 45_000)
        refreshTimer = setTimeout(() => {
          ws?.close(1000, 'token_refresh')
        }, refreshMs)

        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(String(ev.data)) as { kind?: string }
            if (typeof data.kind === 'string' && data.kind.startsWith('notification.')) {
              void queryClient.invalidateQueries({ queryKey: queryKeys.account.notifications() })
              void queryClient.invalidateQueries({ queryKey: queryKeys.account.notificationsUnread() })
            }
          } catch {
            /* ignore non-JSON */
          }
        }

        ws.onclose = () => {
          clearTimers()
          if (!disposed) {
            reconnectTimer = setTimeout(() => {
              void connect()
            }, 2_500)
          }
        }
      } catch {
        if (!disposed) {
          reconnectTimer = setTimeout(() => {
            void connect()
          }, 5_000)
        }
      }
    }

    void connect()

    return () => {
      disposed = true
      clearTimers()
      ws?.close(1000, 'unmount')
    }
  }, [enabled, queryClient])
}

export type InAppNotificationsQueryOptions =
  | boolean
  | { enabled: boolean; unreadOnly?: boolean }

export function useInAppNotifications(options: InAppNotificationsQueryOptions) {
  const resolved =
    typeof options === 'boolean'
      ? { enabled: options, unreadOnly: false }
      : { enabled: options.enabled, unreadOnly: options.unreadOnly ?? false }

  return useInfiniteQuery({
    queryKey: [...queryKeys.account.notifications(), { unreadOnly: resolved.unreadOnly }] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      accountApi.getInAppNotifications({
        limit: 25,
        cursor: pageParam,
        unreadOnly: resolved.unreadOnly ? true : undefined,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor ? JSON.stringify(lastPage.nextCursor) : undefined,
    enabled: resolved.enabled,
  })
}

export function useMarkInAppNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => accountApi.markInAppNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notifications() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notificationsUnread() })
    },
  })
}

export function useMarkAllInAppNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => accountApi.markAllInAppNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notifications() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notificationsUnread() })
    },
  })
}

export function useCreateOrgAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      title: string
      body: string
      actionPath?: string
      forceDelivery?: boolean
    }) => accountApi.createOrgAnnouncement(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notifications() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.account.notificationsUnread() })
    },
  })
}
